/**
 * The keyring: where the data key lives and how it is unlocked. ADR-0006.
 *
 * ## Three states, and what each one honestly means
 *
 * | State         | On disk                        | What it protects against          |
 * | ------------- | ------------------------------ | --------------------------------- |
 * | `unprotected` | DEK stored in the clear        | **Nothing.** Obfuscation only     |
 * | `locked`      | DEK wrapped under a PIN-derived key | Someone holding the database |
 * | `unlocked`    | as `locked`; DEK in memory     | as `locked`, until lock or reload  |
 *
 * `unprotected` is the default, and calling it protection would be a lie: the
 * key sits beside the data, so anyone with the database has both. It exists
 * because a tracker that demanded a PIN before the first log would lose the
 * users this project is for, and because encrypting-with-a-nearby-key still
 * means a casual look through browser storage shows ciphertext.
 *
 * The UI must state this distinction plainly and the limitations list must
 * repeat it. Implying otherwise is the exact overclaim the FTC pursues.
 *
 * ## The DEK never touches localStorage
 *
 * It lives in the IndexedDB `meta` store, deleted by the same wipe that deletes
 * entries (FR-607). A key surviving in localStorage after a "delete everything"
 * would make crypto-shredding a false claim.
 */
import {
  deriveKek,
  exportDek,
  generateDek,
  importDek,
  unwrapDek,
  wrapDek,
  newSalt,
  toBase64,
  fromBase64,
  currentIterations,
  PBKDF2_ITERATIONS,
  type SealedBlob,
} from './crypto'
import { openDb, promisify, STORE, txDone } from './db'

export const KEYRING_KEY = 'keyring'

export type KeyringState = 'unprotected' | 'locked' | 'unlocked'

interface StoredKeyring {
  v: 1
  /** Present when no PIN is set: the DEK in the clear. Obfuscation only. */
  bare?: string
  /** Present when a PIN is set: the DEK wrapped under the PIN-derived key. */
  wrapped?: SealedBlob
  /** Salt and cost for the PIN derivation. Neither is secret. */
  salt?: string
  iterations?: number
}

/**
 * The unlocked key, in memory only.
 *
 * Module-level rather than in a React context because storage functions need it
 * without a component in scope, and because it must be droppable synchronously
 * when the app locks.
 */
let liveDek: CryptoKey | undefined

async function readKeyring(): Promise<StoredKeyring | undefined> {
  const db = await openDb()
  const tx = db.transaction(STORE.meta, 'readonly')
  return promisify<StoredKeyring | undefined>(tx.objectStore(STORE.meta).get(KEYRING_KEY))
}

async function writeKeyring(keyring: StoredKeyring): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(STORE.meta, 'readwrite')
  tx.objectStore(STORE.meta).put(keyring, KEYRING_KEY)
  await txDone(tx)
}

/**
 * Prepare the keyring, creating one on first run.
 *
 * Returns the state rather than throwing, because "locked" is a normal
 * condition the UI handles, not an error.
 */
export async function initKeyring(): Promise<KeyringState> {
  const existing = await readKeyring()

  if (existing === undefined) {
    // First run: generate a DEK and store it bare. No PIN has been offered yet,
    // and refusing to work until one is set would be the wrong trade.
    const dek = await generateDek()
    await writeKeyring({ v: 1, bare: await exportDek(dek) })
    liveDek = dek
    return 'unprotected'
  }

  if (existing.bare !== undefined) {
    liveDek = await importDek(existing.bare)
    return 'unprotected'
  }

  return liveDek === undefined ? 'locked' : 'unlocked'
}

export async function keyringState(): Promise<KeyringState> {
  const existing = await readKeyring()
  if (existing === undefined) return 'unprotected'
  if (existing.bare !== undefined) return 'unprotected'
  return liveDek === undefined ? 'locked' : 'unlocked'
}

export const isPinSet = async (): Promise<boolean> =>
  (await keyringState()) !== 'unprotected'

/**
 * The key for reading and writing records.
 *
 * Returns undefined when locked. Callers must treat that as "not available yet"
 * and not as an error — attempting to read while locked is a programming
 * mistake, but it must not surface as a crash to the user.
 */
export const currentDek = (): CryptoKey | undefined => liveDek

/**
 * Set or change the PIN.
 *
 * Rewraps one key. The records are never touched, which is the entire reason
 * ADR-0006 chose two keys: a half-completed re-encryption of a health record
 * would be a data-loss bug, and this design has no such state.
 *
 * There is deliberately NO recovery path. A "forgot PIN" flow would mean the DEK
 * was recoverable without the PIN, which would mean the encryption was
 * decorative. The UI must say this BEFORE the PIN is set, not after.
 */
export async function setPin(pin: string): Promise<void> {
  const dek = liveDek
  if (dek === undefined) {
    throw new Error('Cannot set a PIN while locked — unlock first')
  }

  const salt = newSalt()
  const iterations = currentIterations()
  const wrapped = await wrapDek(dek, await deriveKek(pin, salt, iterations))

  // The bare copy is dropped in the same write that stores the wrapped one, so
  // there is no window in which both exist on disk.
  await writeKeyring({ v: 1, wrapped, salt: toBase64(salt), iterations })
}

/**
 * Remove the PIN, returning to `unprotected`.
 *
 * Requires the current PIN: without it this would be a way to strip protection
 * from a locked database.
 */
export async function removePin(pin: string): Promise<void> {
  const dek = await unlock(pin)
  await writeKeyring({ v: 1, bare: await exportDek(dek) })
}

/**
 * Unlock with a PIN.
 *
 * Throws when the PIN is wrong. There is no separate verification step and no
 * stored verifier: AES-GCM authenticates the wrapped key, so a wrong PIN simply
 * fails to unwrap. That means there is no check to implement incorrectly and
 * nothing on disk that could confirm a guess offline any faster than unwrapping
 * would.
 */
export async function unlock(pin: string): Promise<CryptoKey> {
  const keyring = await readKeyring()
  if (keyring?.wrapped === undefined || keyring.salt === undefined) {
    throw new Error('No PIN is set')
  }

  const kek = await deriveKek(
    pin,
    fromBase64(keyring.salt),
    keyring.iterations ?? PBKDF2_ITERATIONS,
  )
  // Propagates on a wrong PIN. The caller distinguishes "wrong PIN" from a real
  // fault by the state of the keyring, not by parsing this error.
  const dek = await unwrapDek(keyring.wrapped, kek)
  liveDek = dek
  return dek
}

/** Drop the in-memory key. Synchronous, so locking cannot be interrupted. */
export function lock(): void {
  liveDek = undefined
}

/**
 * Destroy the keyring. FR-607, FR-608, L-06.
 *
 * This is the crypto-shredding step: once the DEK is gone, every sealed record
 * still on disk — and, from v0.5, every copy already replicated to a relay — is
 * permanently unreadable. It is the only deletion guarantee this architecture
 * can actually make, and it is why the key lives in a store the wipe covers.
 */
export async function destroyKeyring(): Promise<void> {
  liveDek = undefined
  const db = await openDb()
  const tx = db.transaction(STORE.meta, 'readwrite')
  tx.objectStore(STORE.meta).delete(KEYRING_KEY)
  await txDone(tx)
}

/** Test seam. Not used by application code. */
export function resetKeyringForTests(): void {
  liveDek = undefined
}
